/** * @type {string|null} Stores the initial set of active OneTrust group IDs.
 */
let otIniGrps = null;

/** * @type {string|undefined} Flag to track if the OneTrust wrapper has initialized.
 */
let OptanonWrapperCount = undefined;

/**
 * Compares initial active groups against current ones and deletes cookies 
 * associated with groups that have been disabled by the user.
 * * @param {string} initOptGroupIds - A comma-separated string of previously active group IDs.
 */
function otDeleteCookie(initOptGroupIds) {
    const { Optanon, OnetrustActiveGroups } = window;

    // Deep clone the domain group data from OneTrust API
    const otDomainGrps = JSON.parse(JSON.stringify(Optanon?.GetDomainData()?.Groups ?? '[]'));

    // Identify which groups were active but are now inactive
    const otDeletedGrpIds = otGetInactiveId(initOptGroupIds, OnetrustActiveGroups);

    // If there are newly disabled groups and domain data is available
    if (otDeletedGrpIds.length != 0 && otDomainGrps.length != 0) {
        otDomainGrps.forEach(domainGroup => {

            // Check top-level Custom Groups (e.g., Performance, Functional)
            if (domainGroup['CustomGroupId'] && otDeletedGrpIds.includes(domainGroup['CustomGroupId'])) {
                domainGroup['Cookies']?.forEach((cookie) => {
                    eraseCookie(cookie['Name']);
                });
            }

            // Check specific Hosts within those groups
            domainGroup['Hosts']?.forEach((host) => {
                if (otDeletedGrpIds.includes(host['HostId']) && host['Cookies']?.length != 0) {
                    host['Cookies']?.forEach((cookie) => {
                        eraseCookie(cookie['Name']);
                    });
                }
            })
        });
    }

    // Refresh the reference point for the next consent change
    otIniGrps = otGetInitialGrps();
}

/**
 * Captures the current state of active OneTrust groups and resets the wrapper counter.
 * * @returns {string} The current string of active group IDs from the window object.
 */
function otGetInitialGrps() {
    OptanonWrapperCount = true;
    return window.OnetrustActiveGroups;
}

/**
 * Aggressively removes a cookie and its corresponding Local Storage entry.
 * It attempts to delete the cookie across multiple paths and domains to ensure removal.
 * * @param {string} name - The name of the cookie/LSO key to remove.
 */
function eraseCookie(name) {
    try {
        let domainName = window.location.hostname;

        // 1. Delete root path cookies (most common)
        let domainLevels = domainName.split('.');
        let domainCount = domainLevels.length - 2;
        // 1.1 Remove current domain and sub domains
        document.cookie = name + '=; Max-Age=-99999999; Path=/;Domain=' + domainName;

        for (let i = 0; i < domainCount; i++) {
            domainLevels.shift();
            document.cookie = name + '=; Max-Age=-99999999; Path=/;Domain=' + `${domainLevels.join('.')}`;
            document.cookie = name + '=; Max-Age=-99999999; Path=/;Domain=' + `.${domainLevels.join('.')}`;
        }

        document.cookie = name + '=; Max-Age=-99999999; Path=/;';

        // 2. Delete Local Storage entry (LSO) if it exists
        localStorage.removeItem(name);

        // 3. Recursive Path Cleanup: Delete cookies that might be set on sub-paths
        let pathArray = window.location.pathname.split('/');

        pathArray.forEach((path, i) => {
            if (path) {
                // Build path incrementally (e.g., /shop, /shop/checkout)
                let currentPath = pathArray.slice(0, i + 1).join('/');

                // Attempt deletion with and without domain, and with/without trailing slash
                document.cookie = name + '=; Max-Age=-99999999; Path=' + currentPath + ';Domain=' + domainName;
                document.cookie = name + '=; Max-Age=-99999999; Path=' + currentPath + ';';
                document.cookie = name + '=; Max-Age=-99999999; Path=' + currentPath + '/;Domain=' + domainName;
                document.cookie = name + '=; Max-Age=-99999999; Path=' + currentPath + '/;';
            }
        })
    } catch (e) {
        console.log(e)
        throw e;
    }

}

/**
 * Compares two initial groups and current OneTrust group. Current groups updated after concent update.
 * * @param {string} initialActiveGroupString - The starting active groups.
 * @param {string} currentActiveGroupString - The new active groups.
 * @returns {string[]} An array of Group IDs which need to be removed.
 */
function otGetInactiveId(initialActiveGroupString, currentActiveGroupString) {
    const initialActiveGroupArray = initialActiveGroupString.split(",").filter(Boolean);
    const currentActiveGroupArray = currentActiveGroupString.split(",").filter(Boolean);

    // Filter for IDs present in the initial set but missing in the current set
    return initialActiveGroupArray.reduce((acc, current) => {
        if (!currentActiveGroupArray.includes(current)) {
            acc.push(current);
        }
        return acc;
    }, []);
}

/**
 * The standard OneTrust callback function. 
 * This executes every time the user saves preferences or the banner loads.
 */
function OptanonWrapper() {
    // If first run, initialize the group tracking
    if (typeof OptanonWrapperCount == "undefined") {
        otIniGrps = otGetInitialGrps();
    }

    // Trigger the deletion logic for any disabled groups
    otDeleteCookie(otIniGrps);
}

/**
 * Exposes the OptanonWrapper to the global window object.
 */
module.exports = function () {
    window.OptanonWrapper = OptanonWrapper;
}